import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiMail, FiCheckCircle, FiXCircle, FiLock } from "react-icons/fi";
import { getAuth } from "firebase/auth";
import { useAppContext } from "../../store/AppContext";
import { activatePreviewAfterVerification } from "../../services/premiumPreview.service";
import { saveUserProfile } from "../../services/userProfile.service";
import { firebaseApp } from "../../services/firebase/firebase.config";
import { Screen } from "../../components/ui/Screen";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import {
  validateActionCode,
  applyEmailVerificationCode,
  confirmPasswordResetWithCode,
} from "../../services/firebase/auth.service";

const auth = getAuth(firebaseApp);

type ActionMode = "verifyEmail" | "resetPassword" | "recoverEmail";
type Status = "loading" | "ready" | "success" | "error";

export function AuthActionScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuthUser, applyEntitlement } = useAppContext();

  const mode = searchParams.get("mode") as ActionMode | null;
  const oobCode = searchParams.get("oobCode");

  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState("");
  const [actionEmail, setActionEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [processing, setProcessing] = useState(false);

  // Validate the action code on mount and extract email
  useEffect(() => {
    async function validate() {
      if (!oobCode) {
        setStatus("error");
        setError("Invalid link. Missing verification code.");
        return;
      }
      if (!mode) {
        setStatus("error");
        setError("Invalid link. Missing action type.");
        return;
      }

      try {
        const info = await validateActionCode(oobCode);
        // Extract email from action code info (available for verifyEmail mode)
        if (info?.data?.email) {
          setActionEmail(info.data.email);
        }
        setStatus("ready");
      } catch (err) {
        console.error("[AuthAction] Code validation failed:", err);
        setStatus("error");
        setError(err instanceof Error ? err.message : "This link is invalid or has expired.");
      }
    }

    validate();
  }, [oobCode, mode]);

  // ---- Email Verification ----
  async function handleVerifyEmail() {
    if (!oobCode) return;
    setProcessing(true);
    setError("");

    try {
      await applyEmailVerificationCode(oobCode);

      // Case 1: User is signed in on this device — refresh and go to app
      const user = auth.currentUser;
      if (user) {
        // Write durable verification timestamp — source of truth, independent of Firebase auth state
        try {
          await saveUserProfile(user.uid, { emailVerificationCompletedAt: Date.now() });
          console.log('[AuthAction] emailVerificationCompletedAt written');
          window.dispatchEvent(new CustomEvent('email-verification-completed', { detail: { userId: user.uid } }));
        } catch (err) {
          console.warn('[AuthAction] Failed to write emailVerificationCompletedAt (non-fatal):', err);
        }

        await user.reload();
        await user.getIdToken(true);

        // Re-read auth.currentUser after reload — gets fresh emailVerified state
        const fresh = auth.currentUser;

        if (!fresh) {
          console.error("[AuthAction] No authenticated user after verification");
          throw new Error("User not authenticated after verification");
        }

        console.log("[AuthAction] Signed-in user verified:", fresh.emailVerified);

        if (!fresh.emailVerified) {
          // Poll briefly in case Firebase propagation lags
          for (let i = 0; i < 5 && !auth.currentUser?.emailVerified; i++) {
            await new Promise(r => setTimeout(r, 600));
            await auth.currentUser?.reload();
          }
          console.log("[AuthAction] After poll, emailVerified:", auth.currentUser?.emailVerified);
        }

        setStatus("success");

        // Reload auth state then call backend endpoint — does not depend on client emailVerified
        try {
          await refreshAuthUser();
          const activationResult = await activatePreviewAfterVerification();
          console.log('[AuthAction] activatePreviewAfterVerification result:', activationResult.status);
          if (activationResult.status === 'activated' || activationResult.status === 'already_active') {
            applyEntitlement(activationResult);
          }
        } catch (err) {
          // Non-fatal — navigate regardless
          console.warn('[AuthAction] preview activation failed (non-fatal):', err);
        }

        setTimeout(() => navigate("/home", { replace: true }), 1500);
        return;
      }

      // Case 2: User is NOT signed in (opened link in different browser/tab)
      // Verification still succeeded server-side. Show success + sign in button.
      console.log("[AuthAction] Signed-out verification succeeded for:", actionEmail);
      setStatus("success");
      // Don't auto-navigate — they need to sign in
    } catch (err) {
      console.error("[AuthAction] Email verification failed:", err);
      setError(err instanceof Error ? err.message : "Failed to verify email. Please try again.");
      setStatus("error");
    } finally {
      setProcessing(false);
    }
  }

  // ---- Password Reset ----
  async function handlePasswordReset() {
    if (!oobCode) return;
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters long."); return; }

    setProcessing(true);
    setError("");

    try {
      await confirmPasswordResetWithCode(oobCode, newPassword);
      setStatus("success");
      setTimeout(() => navigate("/auth/login", { replace: true }), 2000);
    } catch (err) {
      console.error("[AuthAction] Password reset failed:", err);
      setError(err instanceof Error ? err.message : "Failed to reset password. Please try again.");
      setStatus("error");
    } finally {
      setProcessing(false);
    }
  }

  // Navigate to login with prefilled email
  function goToLogin() {
    const params = new URLSearchParams();
    params.set("verified", "1");
    if (actionEmail) params.set("email", actionEmail);
    navigate(`/auth/login?${params.toString()}`, { replace: true });
  }

  // ---- RENDER: Loading ----
  if (status === "loading") {
    return (
      <Screen title="Loading..." subtitle="Validating your link">
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#007C86]" />
          </div>
        </Card>
      </Screen>
    );
  }

  // ---- RENDER: Error ----
  if (status === "error") {
    return (
      <Screen title="Link Error" subtitle="Unable to process your request">
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="rounded-full bg-red-100 p-3">
                <FiXCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">
                {mode === "resetPassword" ? "Password Reset Failed" : "Verification Failed"}
              </p>
              <p className="mt-2 text-xs text-gray-600">
                {error || "This link is invalid or has already been used."}
              </p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-800">
                {mode === "resetPassword"
                  ? "Please request a new password reset email from the login page."
                  : "Please request a new verification email or contact support."}
              </p>
            </div>
            <Button fullWidth onClick={() => navigate("/auth/login", { replace: true })}>
              Go to Login
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  // ---- RENDER: Success ----
  if (status === "success") {
    const isSignedIn = !!auth.currentUser;

    return (
      <Screen title="Success!" subtitle={mode === "resetPassword" ? "Password updated" : "Email verified"}>
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="rounded-full bg-green-100 p-3">
                <FiCheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">
                {mode === "resetPassword" ? "Password Reset Complete" : "Email Verified"}
              </p>
              <p className="mt-1 text-xs text-gray-600">
                {mode === "resetPassword"
                  ? "Your password has been updated. You can now sign in."
                  : isSignedIn
                  ? "Your email has been verified. Redirecting to the app..."
                  : "Your email has been verified. Please sign in to continue."}
              </p>
            </div>

            {/* If signed out, show explicit sign in button */}
            {!isSignedIn && mode === "verifyEmail" && (
              <Button fullWidth onClick={goToLogin}>
                Continue to Sign In
              </Button>
            )}

            {mode === "resetPassword" && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                <p className="text-xs text-green-900">Redirecting to login page...</p>
              </div>
            )}
          </div>
        </Card>
      </Screen>
    );
  }

  // ---- RENDER: Ready — Email Verification ----
  if (mode === "verifyEmail") {
    return (
      <Screen title="Verify Your Email" subtitle="One more step to get started">
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="rounded-full bg-blue-100 p-3">
                <FiMail className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Confirm Your Email Address</p>
              <p className="mt-1 text-xs text-gray-600">
                Click the button below to verify your email and activate your account.
              </p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                <FiXCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-900">{error}</p>
              </div>
            )}

            <Button fullWidth onClick={handleVerifyEmail} disabled={processing}
              leftIcon={processing ? undefined : <FiCheckCircle />}>
              {processing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Verifying...</span>
                </div>
              ) : "Verify My Email"}
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  // ---- RENDER: Ready — Password Reset ----
  if (mode === "resetPassword") {
    return (
      <Screen title="Reset Password" subtitle="Choose a new password">
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="rounded-full bg-blue-100 p-3">
                <FiLock className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Create New Password</p>
              <p className="mt-1 text-xs text-gray-600">Enter a new password for your account.</p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                <FiXCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-900">{error}</p>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label htmlFor="new-password" className="block text-xs font-medium text-gray-700 mb-1">New Password</label>
                <input id="new-password" type="password" placeholder="Enter new password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} disabled={processing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#007C86] focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-700 mb-1">Confirm Password</label>
                <input id="confirm-password" type="password" placeholder="Confirm new password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} disabled={processing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#007C86] focus:border-transparent" />
              </div>
            </div>

            <Button fullWidth onClick={handlePasswordReset} disabled={processing || !newPassword || !confirmPassword}
              leftIcon={processing ? undefined : <FiLock />}>
              {processing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Resetting...</span>
                </div>
              ) : "Reset Password"}
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  // ---- Fallback ----
  return (
    <Screen title="Unknown Action" subtitle="Invalid request">
      <Card>
        <div className="space-y-4 text-center">
          <FiXCircle className="h-8 w-8 text-gray-600 mx-auto" />
          <p className="text-sm text-gray-900">This link type is not recognized.</p>
          <Button fullWidth onClick={() => navigate("/auth/login", { replace: true })}>Go to Login</Button>
        </div>
      </Card>
    </Screen>
  );
}
